import { getAnalytics, logEvent as firebaseLogEvent, isSupported, setUserId as firebaseSetUserId, setUserProperties as firebaseSetUserProperties } from 'firebase/analytics';
import { app } from '../firebase/firebase';
import { log } from '../services/production-logger';


// Flag to indicate if analytics is enabled
let analyticsEnabled = false;
let analyticsInitialized = false;

/**
 * Initialize analytics - attempt to enable only if supported
 */
export async function initAnalytics(): Promise<void> {
  if (analyticsInitialized) return;

  try {
    // Check if analytics is supported in this environment
    const supported = await isSupported();

    if (supported) {
      if (app) {
        getAnalytics(app);
        analyticsEnabled = true;
        log.debug('Firebase Analytics initialized successfully');
      } else {
        log.debug('Firebase app not available for Analytics initialization');
        analyticsEnabled = false;
      }
    } else {
      log.debug(
        'Firebase Analytics not supported in this environment - using console logging instead',
      );
      analyticsEnabled = false;
    }
  } catch (error) {
    log.warn('Error initializing Firebase Analytics - falling back to console logging:', error);
    analyticsEnabled = false;
  } finally {
    analyticsInitialized = true;
  }
}

// Analytics initialization moved to first usage to prevent module-level async calls
// initAnalytics() will be called lazily when first analytics function is used

/**
 * Log an event to analytics
 * @param eventName - Name of the event to log
 * @param eventParams - Optional parameters for the event
 */
export function logEvent(eventName: string, eventParams?: Record<string, any>): void {
  // Ensure analytics is initialized
  if (!analyticsInitialized) {
    initAnalytics().catch(console.error);
  }

  // Always log to console for debugging
  log.debug(`Analytics event logged: ${eventName}`, eventParams);

  // Only attempt to use Firebase Analytics if it's enabled
  if (analyticsEnabled) {
    try {
      if (app) {
        const analytics = getAnalytics(app);
        firebaseLogEvent(analytics, eventName, eventParams);
      }
    } catch (error) {
      // Fail silently when analytics is not available or Firebase not ready
      log.debug('Firebase Analytics error (safe to ignore):', error);
    }
  }
}

/**
 * Track page view
 * @param pageName - Name of the page to track
 * @param pageParams - Optional parameters for the page view
 */
export function trackPageView(pageName: string, pageParams?: Record<string, any>): void {
  logEvent('page_view', {
    page_name: pageName,
    ...pageParams,
  });
}

/**
 * Track user action
 * @param actionName - Name of the action to track
 * @param actionParams - Optional parameters for the action
 */
export function trackUserAction(actionName: string, actionParams?: Record<string, any>): void {
  logEvent('user_action', {
    action_name: actionName,
    ...actionParams,
  });
}

/**
 * Track error
 * @param errorName - Name of the error to track
 * @param errorParams - Optional parameters for the error
 */
export function trackError(errorName: string, errorParams?: Record<string, any>): void {
  logEvent('app_error', {
    error_name: errorName,
    ...errorParams,
  });
}

/**
 * Set user ID for analytics
 * @param userId - User ID to set
 */
export function setUserId(userId: string): void {
  log.debug(`Analytics user ID set: ${userId}`);

  if (analyticsEnabled) {
    try {
      if (app) {
        const analytics = getAnalytics(app);
        firebaseSetUserId(analytics, userId);
      }
    } catch (error) {
      log.debug('Firebase Analytics setUserId error (safe to ignore):', error);
    }
  }
}

/**
 * Set user properties for analytics
 * @param properties - User properties to set
 */
export function setUserProperties(properties: Record<string, any>): void {
  log.debug('Analytics user properties set:', properties);

  if (analyticsEnabled) {
    try {
      if (app) {
        const analytics = getAnalytics(app);
        firebaseSetUserProperties(analytics, properties);
      }
    } catch (error) {
      log.debug('Firebase Analytics setUserProperties error (safe to ignore):', error);
    }
  }
}
